// frontend/src/lib/api/bookingService.ts
import { BaseApiService } from './baseService';
import { ApiResponse } from '../httpClient';
import { ExportBookingsRequest, ExportBookingsResponse } from '@/types/booking';

export interface Booking {
  booking_id: number;
  user_id: number;
  provider_id: number;
  provider_service_id: number;
  service_date: string;
  preferred_time?: string;
  status: 'pending' | 'accepted' | 'rejected' | 'completed' | 'cancelled';
  description?: string;
  business_description?: string;
  pincode: string;
  location: string;
  provider_notes?: string;
  completion_notes?: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
  booking_reference?: string;
  estimated_duration?: string;
  provider_address?: string;
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  customer_address?: string;
  provider_name?: string;
  business_name?: string;
  provider_email?: string;
  provider_phone?: string;
  provider_rating?: number;
  provider_reviews?: number;
  service_name?: string;
  service_price?: string;
  service_description?: string;
  category_name?: string;
  contact_person?: string;
  contact_phone?: string;
  contact_email?: string;
  total_amount?: number;
  rating?: number;
  customer_review?: string;
}

export interface BookingFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  date_from?: string;
  date_to?: string;
  provider_id?: number;
  customer_id?: number;
  service_type?: string;
}

export interface BookingFormData {
  provider_service_id: number;
  service_date: string;
  preferred_time?: string;
  description?: string;
  business_description?: string;
  pincode: string;
  location: string;
}

export interface BookingStatusUpdate {
  status: 'pending' | 'accepted' | 'rejected' | 'completed' | 'cancelled';
  notes?: string;
}

// ✅ TIMELINE INTERFACES
export interface TimelineEntry {
  timeline_id: number;
  booking_id: number;
  status: string;
  title: string;
  description: string;
  notes?: string;
  milestone_type: 'status_change' | 'progress_update' | 'follow_up' | 'milestone' | 'media_upload' | 'system_update';
  progress_percentage?: number;
  created_by: number;
  created_by_role: 'customer' | 'provider' | 'admin' | 'system';
  created_by_name?: string;
  is_visible_to_customer: boolean;
  is_visible_to_provider: boolean;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  created_at: string;
  updated_at: string;
}

export interface CreateTimelineEntryData {
  title: string;
  description?: string;
  milestone_type?: 'status_change' | 'progress_update' | 'follow_up' | 'milestone' | 'media_upload' | 'system_update';
  progress_percentage?: number;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  notes?: string;
  is_visible_to_customer?: boolean;
  is_visible_to_provider?: boolean;
}

export interface UpdateTimelineEntryData {
  title?: string;
  description?: string;
  notes?: string;
  progress_percentage?: number;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
}

// ✅ FOLLOW-UPS INTERFACES
export interface FollowUpEntry {
  follow_up_id: number;
  booking_id: number;
  follow_up_type: 'reminder' | 'inspection' | 'payment' | 'feedback' | 'warranty' | 'maintenance' | 'callback';
  title: string;
  description?: string;
  due_date: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'pending' | 'completed' | 'cancelled';
  assigned_to?: number;
  assigned_role?: 'customer' | 'provider' | 'admin';
  created_by: number;
  created_by_role: 'customer' | 'provider' | 'admin';
  completion_notes?: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateFollowUpData {
  follow_up_type: 'reminder' | 'inspection' | 'payment' | 'feedback' | 'warranty' | 'maintenance' | 'callback';
  title: string;
  description?: string;
  due_date: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  assigned_to?: number;
  assigned_role?: 'customer' | 'provider' | 'admin';
}

export interface UpdateFollowUpData {
  title?: string;
  description?: string;
  due_date?: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  assigned_to?: number;
  assigned_role?: 'customer' | 'provider' | 'admin';
}

export interface CompleteFollowUpData {
  completion_notes?: string;
}

class BookingService extends BaseApiService {
  constructor() {
    super({
      baseEndpoint: '/bookings',
      timeout: 10000,
    });
  }

  async getBookings(filters: BookingFilters = {}): Promise<ApiResponse<any>> {
    return this.get('', { params: filters });
  }

  async getBookingById(id: number): Promise<ApiResponse<Booking>> {
    return this.get(`/${id}`);
  }

  async createBooking(data: BookingFormData): Promise<ApiResponse<{ booking: Booking }>> {
    return this.post('', data);
  }

  async updateBookingStatus(id: number, statusUpdate: BookingStatusUpdate): Promise<ApiResponse<{ booking: Booking }>> {
    return this.put(`/${id}/status`, statusUpdate);
  }

  async cancelBooking(id: number, reason?: string): Promise<ApiResponse<{ booking: Booking }>> {
    return this.post(`/${id}/cancel`, { reason });
  }

  async rescheduleBooking(id: number, newDateTime: string, reason?: string): Promise<ApiResponse<{ booking: Booking }>> {
    return this.put(`/${id}/reschedule`, { new_date_time: newDateTime, reason });
  }

  async completeBooking(id: number, completionNotes?: string, serviceDetails?: any): Promise<ApiResponse<{ booking: Booking }>> {
    return this.post(`/${id}/complete`, { completion_notes: completionNotes, service_details: serviceDetails });
  }

  async getBookingStats(period: string = 'monthly'): Promise<ApiResponse<any>> {
    return this.get('/stats', { params: { period } });
  }

  // ✅ ADDED: Missing exportBookings method
  async exportBookings(request: ExportBookingsRequest): Promise<ApiResponse<ExportBookingsResponse['data']>> {
    const { filters, format = 'csv', fields } = request;
    
    const params: Record<string, any> = {
      format,
      ...(fields && { fields: fields.join(',') }),
      ...filters
    };

    return this.get('/export', { params });
  }

  async bulkUpdateBookings(bookingIds: number[], updates: Partial<BookingStatusUpdate>): Promise<ApiResponse<any>> {
    return this.post('/bulk-update', { booking_ids: bookingIds, ...updates });
  }

  async searchBookings(searchQuery: string, filters: BookingFilters = {}): Promise<ApiResponse<any>> {
    return this.get('/search', { params: { q: searchQuery, ...filters } });
  }

  async getBookingHistory(userId: number, userType: 'customer' | 'provider'): Promise<ApiResponse<any>> {
    return this.get('/history', { params: { user_id: userId, user_type: userType } });
  }

  async addBookingNote(id: number, note: string, isInternal?: boolean): Promise<ApiResponse<any>> {
    return this.post(`/${id}/notes`, { note, is_internal: isInternal });
  }

  async uploadBookingAttachment(id: number, file: File, description?: string): Promise<ApiResponse<any>> {
    const formData = new FormData();
    formData.append('file', file);
    if (description) formData.append('description', description);
    return this.post(`/${id}/attachments`, formData);
  }
  // ✅ ADDED: Missing rateBooking method
  async rateBooking(bookingId: number, rating: number, review?: string): Promise<ApiResponse<{ booking: Booking }>> {
    return this.post(`/${bookingId}/rate`, { rating, review });
  }

  // ✅ ADDED: Missing bulkUpdateBookingStatus method
  async bulkUpdateBookingStatus(bookingIds: number[], statusUpdate: BookingStatusUpdate): Promise<ApiResponse<any>> {
    return this.post('/bulk-update-status', { booking_ids: bookingIds, ...statusUpdate });
  }

  // ✅ TIMELINE METHODS - Following standard service patterns
  async getTimeline(bookingId: number, page: number = 1, limit: number = 20): Promise<ApiResponse<{ timeline: TimelineEntry[], pagination: any }>> {
    return this.get(`/${bookingId}/timeline`, { params: { page, limit } });
  }

  async addTimelineEntry(bookingId: number, entry: CreateTimelineEntryData): Promise<ApiResponse<TimelineEntry>> {
    return this.post(`/${bookingId}/timeline`, entry);
  }

  async updateTimelineEntry(bookingId: number, timelineId: number, entry: UpdateTimelineEntryData): Promise<ApiResponse<TimelineEntry>> {
    return this.put(`/${bookingId}/timeline/${timelineId}`, entry);
  }

  async deleteTimelineEntry(bookingId: number, timelineId: number): Promise<ApiResponse<void>> {
    return this.delete(`/${bookingId}/timeline/${timelineId}`);
  }

  // ✅ FOLLOW-UPS METHODS - Following standard service patterns
  async getFollowUps(bookingId: number): Promise<ApiResponse<FollowUpEntry[]>> {
    return this.get(`/${bookingId}/follow-ups`);
  }

  async createFollowUp(bookingId: number, followUp: CreateFollowUpData): Promise<ApiResponse<FollowUpEntry>> {
    return this.post(`/${bookingId}/follow-ups`, followUp);
  }

  async updateFollowUp(bookingId: number, followUpId: number, followUp: UpdateFollowUpData): Promise<ApiResponse<FollowUpEntry>> {
    return this.put(`/${bookingId}/follow-ups/${followUpId}`, followUp);
  }

  async completeFollowUp(bookingId: number, followUpId: number, completionData: CompleteFollowUpData): Promise<ApiResponse<FollowUpEntry>> {
    return this.post(`/${bookingId}/follow-ups/${followUpId}/complete`, completionData);
  }

  async deleteFollowUp(bookingId: number, followUpId: number): Promise<ApiResponse<void>> {
    return this.delete(`/${bookingId}/follow-ups/${followUpId}`);
  }
}

export const bookingService = new BookingService();